import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Download, FileText, ScrollText, Loader2, ShieldCheck } from "lucide-react";

export default function PatientPrivacy() {
  const [npp, setNpp] = useState({ content: "", version: 0, updated_at: "" });
  const [disclosures, setDisclosures] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadDisclosures = () => api.get("/patient/disclosures").then((r) => setDisclosures(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/patient/npp").then((r) => setNpp(r.data)).catch(() => {});
    loadDisclosures();
  }, []);

  const downloadRecords = async () => {
    setDownloading(true); setMsg(""); setErr("");
    try {
      const { data } = await api.get("/patient/records/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-health-records-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg("Your records have been downloaded. This request was logged for your records.");
      loadDisclosures();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    setDownloading(false);
  };

  return (
    <div className="space-y-6" data-testid="patient-privacy-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Privacy &amp; Records</h1>
        <p className="text-sm text-muted-foreground mt-1">Your health information, your rights.</p>
      </div>

      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="privacy-msg">{msg}</p>}
      {err && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="privacy-err">{err}</p>}

      {/* Right of access */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1"><Download className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Download your health records</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Under your HIPAA right of access, you can download a complete copy of your designated record set — profile, treatment plans, labs, appointments, payments and check-ins.</p>
        <button onClick={downloadRecords} disabled={downloading} data-testid="download-records-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download my records (JSON)
        </button>
      </div>

      {/* Accounting of disclosures */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1"><ScrollText className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Accounting of disclosures</h3></div>
        <p className="text-sm text-muted-foreground mb-4">A record of when and to whom your information has been disclosed.</p>
        <div className="divide-y divide-border" data-testid="patient-disclosure-list">
          {disclosures.length === 0 ? (
            <p className="text-sm text-muted-foreground">No disclosures of your information have been recorded.</p>
          ) : disclosures.map((d) => (
            <div key={d.id} className="py-2.5">
              <p className="text-sm font-medium">{d.recipient} <span className="text-xs text-muted-foreground">({d.recipient_type})</span></p>
              <p className="text-xs text-muted-foreground">{d.purpose} · {new Date(d.date).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Notice of Privacy Practices */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1"><FileText className="w-5 h-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Notice of Privacy Practices</h3>
          {npp.version > 0 && <span className="ml-1 text-xs font-medium text-muted-foreground bg-secondary rounded-full px-2.5 py-0.5">v{npp.version}</span>}</div>
        {npp.content ? (
          <pre className="text-sm whitespace-pre-wrap font-sans text-foreground mt-3" data-testid="patient-npp-content">{npp.content}</pre>
        ) : (
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-2"><ShieldCheck className="w-4 h-4" /> Your clinic has not published a Notice of Privacy Practices yet.</p>
        )}
      </div>
    </div>
  );
}
